import { MeetingRecord } from './meeting-record.interface';
import { User } from './user.model';

export interface PotentialClientOrigin {
  id: number;
  name: string;
  slug: string;
  created_at?: Date;
  updated_at?: Date;
}

export interface PotentialClientAction {
  id: number;
  potential_client_id: number;
  user_id: number;
  type: PotentialClientActionType;
  notes?: string | null;
  created_at: Date;
  updated_at?: Date;
  user?: User;
}

export type PotentialClientActionType = 'call' | 'call_no_answer' | 'email_sent' | 'whatsapp_sent' | 'meeting_rescheduled';

export type PotentialClientRejectionReason = 'does_not_qualify' | 'unreachable' | 'wrong_contact' | 'duplicate_contact' | 'does_not_exist';

export const POTENTIAL_CLIENT_ACTION_TYPES: {
  value: PotentialClientActionType;
  label: string;
  icon: string;
  countKey: 'call_count' | 'call_no_answer_count' | 'email_sent_count' | 'whatsapp_sent_count';
}[] = [
  { value: 'call', label: 'Llamada', icon: 'phone', countKey: 'call_count' },
  { value: 'call_no_answer', label: 'Llamada no contesta', icon: 'phone_missed', countKey: 'call_no_answer_count' },
  { value: 'email_sent', label: 'Correo enviado', icon: 'mail', countKey: 'email_sent_count' },
  { value: 'whatsapp_sent', label: 'WhatsApp enviado', icon: 'whatsapp', countKey: 'whatsapp_sent_count' },
];

export const POTENTIAL_CLIENT_ACTION_TYPES_ALL: { value: PotentialClientActionType; label: string }[] = [
  ...POTENTIAL_CLIENT_ACTION_TYPES.map(({ value, label }) => ({ value, label })),
  { value: 'meeting_rescheduled', label: 'Reunión reprogramada' },
];

export const POTENTIAL_CLIENT_REJECTION_REASONS: { value: PotentialClientRejectionReason; label: string }[] = [
  { value: 'does_not_qualify', label: 'No califica' },
  { value: 'unreachable', label: 'Imposible contactar' },
  { value: 'wrong_contact', label: 'Contacto por error' },
  { value: 'duplicate_contact', label: 'Contacto repetido' },
  { value: 'does_not_exist', label: 'No existe' },
];

export const POTENTIAL_CLIENT_REJECTED_STATUS = 'rejected';
export const POTENTIAL_CLIENT_STANDBY_STATUS = 'standby';
export const POTENTIAL_CLIENT_CLOSED_LOST_STATUS = 'closed_lost';

export interface CommercialDashboardRejectionReason {
  reason: string;
  label: string;
  count: number;
  percentage: number | null;
}

export interface CommercialDashboardMetaAdStatusCount {
  slug: string;
  label: string;
  count: number;
}

export interface CommercialDashboardMetaAdBreakdown {
  ad_id: string | null;
  ad_name?: string | null;
  campaign_name?: string | null;
  label: string;
  count: number;
  percentage: number | null;
  by_status?: CommercialDashboardMetaAdStatusCount[];
}

export interface CommercialDashboardMetrics {
  date: string | null;
  leads_assigned: number;
  leads_contacted: number;
  leads_qualified: number;
  leads_not_qualified: number;
  leads_scheduled: number;
  calls_scheduled: number;
  calls_rescheduled: number;
  calls_net: number;
  calls_done: number;
  offers_presented: number;
  closes: number;
  billed_amount: number;
  cash_collected: number;
  ratio_lead_to_scheduled: number | null;
  ratio_call_to_offer: number | null;
  ratio_offer_to_close: number | null;
  ratio_call_to_close: number | null;
  ratio_lead_to_close: number | null;
  revenue_per_lead: number | null;
  revenue_per_close: number | null;
  cash_collect_ratio: number | null;
  rejection_reasons?: CommercialDashboardRejectionReason[];
  leads_by_meta_ad?: CommercialDashboardMetaAdBreakdown[];
}

export interface PotentialClient {
  id: number;
  full_name: string;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  potential_client_status_slug: string;
  origin_id: number | null;
  meta_leadgen_id?: string | null;
  meta_ad_id?: string | null;
  meta_ad_name?: string | null;
  meta_form_id?: string | null;
  meta_campaign_name?: string | null;
  /** When the lead submitted the Meta form (Graph created_time). */
  meta_form_submitted_at?: Date | null;
  commercial_id: number;
  description: string | null;
  approximate_company_annual_billing_in_millions: number | null;
  rejection_reason: PotentialClientRejectionReason | null;
  rejection_justification: string | null;
  rejected_at: Date | null;
  rejected_by_user_id: number | null;
  commercial_notified_by_status_inaction: boolean;
  standby_notify_at: Date | null;
  potential_client_status: PotentialClientStatus;
  origin?: PotentialClientOrigin | null;
  created_at: Date;
  updated_at: Date;
  commercial: User;
  meeting_records: MeetingRecord[];
  potential_client_status_change_records: PotentialClientStatusChangeRecord[];
  potential_client_comments: PotentialClientComment[];
  potential_client_actions?: PotentialClientAction[];
  potential_client_actions_count?: number;
  call_count?: number;
  call_no_answer_count?: number;
  email_sent_count?: number;
  whatsapp_sent_count?: number;
}

export interface PotentialClientStatus {
  id: number;
  slug: string;
  name: string;
  days_to_be_notified_by_inactivity: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface PotentialClientStatusChangeRecord {
  id: number;
  user_id: number;
  old_status_slug: string;
  new_status_slug: string;
  potential_client_id: number;
  old_status: PotentialClientStatus;
  new_status: PotentialClientStatus;
  user: User;
  created_at: Date;
  updated_at: Date;
}

export interface PotentialClientComment {
  id: number;
  author_id: number;
  potential_client_id: number;
  content: string;
  created_at: Date;
  updated_at: Date;
  author: User;
}

export interface LeadAssignmentWeightRow {
  user_id: number;
  name: string;
  surnames: string | null;
  percentage: number;
  meta_leads_assigned: number;
  is_admin: boolean;
  is_commercial: boolean;
}

export interface LeadAssignmentWeightHistoryEntry {
  user_id: number;
  name: string;
  surnames: string | null;
  percentage: number;
}

export interface LeadAssignmentWeightHistoryPeriod {
  start_date: string;
  end_date: string | null;
  weights: LeadAssignmentWeightHistoryEntry[];
}

export interface PotentialClientSearchResult {
  id: number;
  full_name: string;
  company_name: string | null;
}
